import type { HTMLAttributes, KeyboardEvent, MouseEvent, SyntheticEvent } from "react";

/**
 * A11y clavier / souris partagée.
 *
 * **`listboxOptionRowHandlersFromConfig`** : à réutiliser pour toute ligne `role="option"` dans un `role="listbox"`
 * (clic + Enter/Espace, option `onMouseDown` type combobox). Usages existants :
 * `DropdownMenu/dropdownMenuOptionRowHandlers`, `SelectFieldOldListbox`, listes confrères
 * (`ConfrereCollaboratorsList`, `ConfrereOfficeSuggestionsListView`).
 */

/**
 * Clavier pour éléments avec role="button" (Enter / Espace).
 * @param onActivate — typiquement le même handler que onClick
 */
export function createActivationKeyDownHandler<E extends HTMLElement>(
	onActivate: (e: SyntheticEvent<E>) => void,
): (e: KeyboardEvent<E>) => void {
	return (e: KeyboardEvent<E>): void => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			onActivate(e as unknown as SyntheticEvent<E>);
		}
	};
}

/** Même action que onClick pour les événements clavier (jsx-a11y). */
export function onClickToKeyDownHandler<E extends HTMLElement>(
	onClick: (e: MouseEvent<E>) => void,
): (e: KeyboardEvent<E>) => void {
	return createActivationKeyDownHandler<E>((se) => onClick(se as unknown as MouseEvent<E>));
}

export type ListboxOptionRowHandlersConfig = {
	/** Les modules SCSS typent souvent les clés comme `string | undefined`. */
	className: string | undefined;
	tabIndex: -1 | 0;
	/** Comportement type menu déroulant : évite le blur combobox avant clic. */
	mouseDownPreventDefault: boolean;
	allowKeyboardActivate: boolean;
	onActivate: () => void;
};

export type ListboxOptionRowHandlers = Pick<
	HTMLAttributes<HTMLDivElement>,
	"className" | "tabIndex" | "onClick" | "onMouseDown" | "onKeyDown"
>;

/**
 * Handlers communs pour une ligne `role="option"` dans un `role="listbox"`.
 * À combiner dans le même JSX que le listbox avec `role="option"` et littéraux `aria-*` sur la `div`.
 */
export function listboxOptionRowHandlersFromConfig(config: ListboxOptionRowHandlersConfig): ListboxOptionRowHandlers {
	const { className, tabIndex, mouseDownPreventDefault, allowKeyboardActivate, onActivate } = config;
	const resolvedClassName = className ?? "";
	const keyHandler = createActivationKeyDownHandler<HTMLDivElement>((_e) => {
		onActivate();
	});
	return {
		className: resolvedClassName,
		tabIndex,
		onClick: (): void => {
			onActivate();
		},
		onMouseDown: (event: MouseEvent<HTMLDivElement>): void => {
			if (!mouseDownPreventDefault) return;
			event.preventDefault();
		},
		onKeyDown: (event: KeyboardEvent<HTMLDivElement>): void => {
			if (!allowKeyboardActivate) return;
			keyHandler(event);
		},
	};
}
